import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, LogOut, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminSettings from "@/components/AdminSettings";

const STATUSES = ["in attesa", "confermata", "completata", "annullata"];
const STATUS_COLORS = {
  "in attesa": "text-amber-300 border-amber-400/30 bg-amber-400/10",
  "confermata": "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  "completata": "text-sky-300 border-sky-400/30 bg-sky-400/10",
  "annullata": "text-rose-300 border-rose-400/30 bg-rose-400/10",
};
const CATS = ["Consulti Individuali", "Trattamenti Energetici", "Formazione Lux Academy", "Percorsi Biblici"];
const field = "w-full bg-[#FAF3E4] border border-[#2A1F14]/15 rounded-lg px-3 py-2 text-sm text-[#2A1F14] focus:border-gold outline-none transition-colors placeholder:text-[#2A1F14]/30";

function StatCard({ label, value }) {
  return (
    <div className="glass rounded-xl p-5">
      <p className="font-display text-4xl text-gold">{value}</p>
      <p className="text-[#2A1F14]/50 text-xs uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

function StatusSelect({ value, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className={`text-xs rounded-full border px-3 py-1.5 outline-none ${STATUS_COLORS[value] || "text-[#2A1F14]/70 border-[#2A1F14]/20 bg-[#2A1F14]/10"}`}>
      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#EFE3CE] text-[#2A1F14]">{s}</option>)}
    </select>
  );
}

// ---------- Prenotazioni / Iscrizioni / Contatti table ----------
function SubmissionsTable({ endpoint, columns, testidPrefix }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/admin/${endpoint}`).then((r) => setRows(r.data)).catch(() => {}), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/${endpoint}/${id}`, { status }); setRows((r) => r.map((x) => x.id === id ? { ...x, status } : x)); toast.success("Stato aggiornato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/${endpoint}/${id}`); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Eliminato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <button onClick={load} className="text-[#2A1F14]/50 hover:text-gold text-xs inline-flex items-center gap-1.5 mb-4"><RefreshCw size={13} /> Aggiorna</button>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testidPrefix}-table`}>
          <thead><tr className="text-left text-[#2A1F14]/40 text-xs uppercase tracking-wider border-b border-[#2A1F14]/10">
            {columns.map((c) => <th key={c.key} className="py-3 pr-4 font-normal">{c.label}</th>)}
            <th className="py-3 pr-4 font-normal">Stato</th><th className="py-3 font-normal"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={columns.length + 2} className="py-8 text-[#2A1F14]/40 text-center">Nessun dato.</td></tr>}
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-[#2A1F14]/5 hover:bg-[#2A1F14]/[0.03]" data-testid={`${testidPrefix}-row`}>
                {columns.map((c) => <td key={c.key} className="py-3 pr-4 text-[#2A1F14]/75 align-top max-w-[220px]">{row[c.key] || "—"}</td>)}
                <td className="py-3 pr-4 align-top"><StatusSelect value={row.status || "in attesa"} onChange={(s) => updateStatus(row.id, s)} testid={`${testidPrefix}-status-${row.id}`} /></td>
                <td className="py-3 align-top"><button onClick={() => remove(row.id)} data-testid={`${testidPrefix}-del-${row.id}`} className="text-[#2A1F14]/40 hover:text-rose-400 transition-colors"><Trash2 size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Clienti ----------
function Clients() {
  const [clients, setClients] = useState([]);
  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
      {clients.length === 0 && <p className="text-[#2A1F14]/40">Nessun cliente ancora.</p>}
      {clients.map((c) => (
        <div key={c.email} className="glass rounded-xl p-6" data-testid="client-card">
          <h4 className="font-display text-xl text-[#2A1F14]">{c.name || "—"}</h4>
          <p className="text-gold text-sm">{c.email}</p>
          {c.phone && <p className="text-[#2A1F14]/50 text-sm mt-1">{c.phone}</p>}
          <div className="flex gap-3 mt-4 text-xs">
            <span className="text-[#2A1F14]/60 border border-[#2A1F14]/15 rounded-full px-3 py-1">{(c.bookings || []).length} prenotazioni</span>
            <span className="text-[#2A1F14]/60 border border-[#2A1F14]/15 rounded-full px-3 py-1">{(c.courses || []).length} corsi</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Image upload helper ----------
function ImageUpload({ value, onChange, testid }) {
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) { toast.error("Immagine troppo grande (max 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(f);
  };
  return (
    <div className="space-y-2">
      {value && <img src={value} alt="" className="w-full h-32 object-cover rounded-lg border border-[#2A1F14]/10" />}
      <div className="flex gap-2">
        <input placeholder="URL immagine" value={value?.startsWith("data:") ? "" : (value || "")} onChange={(e) => onChange(e.target.value)} className={field} />
        <label className="shrink-0 px-3 py-2 rounded-lg border border-[#2A1F14]/15 text-[#2A1F14]/70 text-sm cursor-pointer hover:border-gold hover:text-gold transition-colors">
          Carica<input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={testid} />
        </label>
      </div>
    </div>
  );
}

// ---------- Servizi manager ----------
const EMPTY_SERVICE = { title: "", category: CATS[0], subtitle: "", description: "", duration: "", price: "", image: "", benefits: [], faq: [], active: true, order: 0 };
function ServicesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...editing, benefits: typeof editing.benefits === "string" ? editing.benefits.split("\n").filter(Boolean) : editing.benefits };
      if (editing.id) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Servizio salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo servizio?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Servizio eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = (s) => { setEditing(s ? { ...s, benefits: (s.benefits || []).join("\n") } : { ...EMPTY_SERVICE }); setOpen(true); };

  return (
    <div>
      <button onClick={() => openEdit(null)} data-testid="service-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors mb-6"><Plus size={16} /> Nuovo servizio</button>
      <div className="space-y-2">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between border border-[#2A1F14]/10 rounded-xl px-5 py-4" data-testid="service-manage-row">
            <div className="min-w-0">
              <p className="text-[#2A1F14] truncate">{s.title}</p>
              <p className="text-[#2A1F14]/40 text-xs">{s.category} · {s.duration} · {s.price}</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={() => openEdit(s)} data-testid={`svc-edit-${s.id}`} className="text-[#2A1F14]/50 hover:text-gold"><Pencil size={16} /></button>
              <button onClick={() => remove(s.id)} data-testid={`svc-del-${s.id}`} className="text-[#2A1F14]/50 hover:text-rose-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#EFE3CE] border border-[#2A1F14]/10 text-[#2A1F14] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              <input placeholder="Titolo" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={field} data-testid="svc-title" />
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={field}>
                {CATS.map((c) => <option key={c} className="bg-[#EFE3CE]">{c}</option>)}
              </select>
              <input placeholder="Sottotitolo" value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} className={field} />
              <textarea placeholder="Descrizione" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={field + " resize-none"} />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Durata (es. 60 min)" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} className={field} />
                <input placeholder="Prezzo (es. € 90)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className={field} data-testid="svc-price" />
              </div>
              <ImageUpload value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} testid="svc-image-file" />
              <textarea placeholder="Benefici (uno per riga)" rows={3} value={editing.benefits} onChange={(e) => setEditing({ ...editing, benefits: e.target.value })} className={field + " resize-none"} />
            </div>
          )}
          <DialogFooter><button onClick={save} data-testid="svc-save" className="px-6 py-2.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Salva</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Blog manager ----------
const EMPTY_POST = { title: "", category: "Spiritualità", excerpt: "", cover: "", author: "Davide Bellafiore", body: "", published: true };
const BLOG_CATS = ["Spiritualità", "Crescita personale", "Energia", "Registri Akashici", "Meditazione", "Eventi Lux Eterna"];
function BlogManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title) { toast.error("Titolo obbligatorio"); return; }
    try {
      if (editing.id) await api.put(`/admin/blog/${editing.id}`, editing);
      else await api.post("/admin/blog", editing);
      toast.success("Articolo salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    try { await api.delete(`/admin/blog/${id}`); toast.success("Articolo eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = async (post) => {
    if (post) { const full = await api.get(`/blog/${post.slug}`).then((r) => r.data).catch(() => post); setEditing(full); }
    else setEditing({ ...EMPTY_POST });
    setOpen(true);
  };

  return (
    <div>
      <button onClick={() => openEdit(null)} data-testid="post-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors mb-6"><Plus size={16} /> Nuovo articolo</button>
      <div className="space-y-2">
        {items.map((b) => (
          <div key={b.id} className="flex items-center justify-between border border-[#2A1F14]/10 rounded-xl px-5 py-4" data-testid="post-manage-row">
            <div className="min-w-0">
              <p className="text-[#2A1F14] truncate">{b.title}</p>
              <p className="text-[#2A1F14]/40 text-xs">{b.category} · {b.date} · {b.published ? "pubblicato" : "bozza"}</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={() => openEdit(b)} data-testid={`post-edit-${b.id}`} className="text-[#2A1F14]/50 hover:text-gold"><Pencil size={16} /></button>
              <button onClick={() => remove(b.id)} data-testid={`post-del-${b.id}`} className="text-[#2A1F14]/50 hover:text-rose-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#EFE3CE] border border-[#2A1F14]/10 text-[#2A1F14] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              <input placeholder="Titolo" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={field} data-testid="post-title" />
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={field}>
                {BLOG_CATS.map((c) => <option key={c} className="bg-[#EFE3CE]">{c}</option>)}
              </select>
              <input placeholder="Autore" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className={field} />
              <textarea placeholder="Estratto" rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className={field + " resize-none"} />
              <p className="text-[#2A1F14]/50 text-xs">Immagine di copertina</p>
              <ImageUpload value={editing.cover} onChange={(v) => setEditing({ ...editing, cover: v })} testid="post-cover-file" />
              <textarea placeholder="Testo dell'articolo (paragrafi separati da invio)" rows={8} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={field + " resize-none"} data-testid="post-body" />
              <label className="flex items-center gap-2 text-sm text-[#2A1F14]/70"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Pubblicato</label>
            </div>
          )}
          <DialogFooter><button onClick={save} data-testid="post-save" className="px-6 py-2.5 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">Salva</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="pt-28 pb-24 min-h-screen constellation">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Dashboard Amministratore</p>
            <h1 className="font-display text-4xl sm:text-5xl text-[#2A1F14]">Gestione LUX AETERNA</h1>
            <p className="text-[#2A1F14]/50 mt-2">{user?.email}</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#2A1F14]/15 text-[#2A1F14]/70 hover:border-gold hover:text-gold transition-colors text-sm"><LogOut size={16} /> Esci</button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-12">
            <StatCard label="Prenotazioni" value={stats.bookings} />
            <StatCard label="In attesa" value={stats.bookings_pending} />
            <StatCard label="Iscrizioni" value={stats.enrollments} />
            <StatCard label="Contatti" value={stats.contacts} />
            <StatCard label="Clienti" value={stats.clients} />
            <StatCard label="Servizi" value={stats.services} />
            <StatCard label="Articoli" value={stats.articles} />
          </div>
        )}

        <Tabs defaultValue="prenotazioni">
          <TabsList className="bg-[#2A1F14]/5 border border-[#2A1F14]/10 flex-wrap h-auto">
            {["prenotazioni", "iscrizioni", "contatti", "clienti", "servizi", "blog", "analytics", "impostazioni"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="data-[state=active]:bg-gold data-[state=active]:text-black capitalize">{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="prenotazioni" className="mt-8">
            <SubmissionsTable endpoint="bookings" testidPrefix="booking" columns={[
              { key: "name", label: "Cliente" }, { key: "email", label: "Email" }, { key: "service", label: "Servizio" },
              { key: "preferred_date", label: "Data pref." }, { key: "phone", label: "Telefono" },
            ]} />
          </TabsContent>
          <TabsContent value="iscrizioni" className="mt-8">
            <SubmissionsTable endpoint="enrollments" testidPrefix="enroll" columns={[
              { key: "name", label: "Cliente" }, { key: "email", label: "Email" }, { key: "course", label: "Corso" }, { key: "mode", label: "Modalità" },
            ]} />
          </TabsContent>
          <TabsContent value="contatti" className="mt-8">
            <SubmissionsTable endpoint="contacts" testidPrefix="contact" columns={[
              { key: "name", label: "Nome" }, { key: "email", label: "Email" }, { key: "subject", label: "Oggetto" }, { key: "message", label: "Messaggio" },
            ]} />
          </TabsContent>
          <TabsContent value="clienti" className="mt-8"><Clients /></TabsContent>
          <TabsContent value="servizi" className="mt-8"><ServicesManager /></TabsContent>
          <TabsContent value="blog" className="mt-8"><BlogManager /></TabsContent>
          <TabsContent value="analytics" className="mt-8"><AdminAnalytics /></TabsContent>
          <TabsContent value="impostazioni" className="mt-8"><AdminSettings /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
